import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ToolPermission } from '@/types/auth'
import { useAuthStore } from './auth'
import apiClient from '@/services/api'

export interface Tool {
  id: number
  name: string
  description: string
  path: string
  icon: string
  route: string
  is_active: boolean
  permissions: {
    can_view: boolean
    can_edit: boolean
    can_admin: boolean
  }
}

export interface ToolGroup {
  id: number
  name: string
  description: string
  is_active: boolean
  tools: Tool[]
}

export const useToolsStore = defineStore('tools', () => {
  // State
  const tools = ref<Tool[]>([])
  const toolGroups = ref<ToolGroup[]>([])
  const userPermissions = ref<ToolPermission[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const activeTools = computed(() => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated) {
      console.log('[ToolsStore] activeTools: User not authenticated')
      return []
    }
    
    console.log('[ToolsStore] activeTools: Filtering tools...')
    console.log('[ToolsStore] activeTools: Available tools:', tools.value.length)
    console.log('[ToolsStore] activeTools: Available permissions:', userPermissions.value.length)
    
    // FALLBACK: Wenn Permission-Mapping fehlschlägt (tool_id undefined), 
    // aber User Admin-Permissions hat, zeige alle aktiven Tools
    const hasValidPermissions = userPermissions.value.some(p => p.tool_id !== undefined)
    const hasAdminPermissions = userPermissions.value.some(p => p.can_view === true)
    
    if (!hasValidPermissions && hasAdminPermissions) {
      console.log('[ToolsStore] activeTools: FALLBACK - Permission mapping failed, but user has admin permissions')
      console.log('[ToolsStore] activeTools: Showing all active tools for admin user')
      const adminTools = tools.value.filter(tool => tool.is_active)
      console.log('[ToolsStore] activeTools: Admin fallback tools:', adminTools.length)
      return adminTools
    }
    
    // Normale Permission-basierte Filterung
    const filtered = tools.value.filter(tool => {
      console.log(`[ToolsStore] activeTools: Checking tool ${tool.id} (${tool.name})`)
      console.log(`[ToolsStore] activeTools: Tool is_active:`, tool.is_active)
      
      const permission = userPermissions.value.find(p => {
        console.log(`[ToolsStore] activeTools: Comparing permission tool_id ${p.tool_id} with tool.id ${tool.id}`)
        return p.tool_id === tool.id
      })
      
      console.log(`[ToolsStore] activeTools: Found permission:`, permission)
      if (permission) {
        console.log(`[ToolsStore] activeTools: Permission can_view:`, permission.can_view)
      }
      
      const result = tool.is_active && permission && permission.can_view
      console.log(`[ToolsStore] activeTools: Tool ${tool.name} included:`, result)
      
      return result
    })
    
    console.log('[ToolsStore] activeTools: Final filtered tools:', filtered.length)
    return filtered
  })

  const adminTools = computed(() => {
    const authStore = useAuthStore()
    
    if (!authStore.isAdmin) {
      return []
    }
    
    return tools.value.filter(tool => {
      const permission = userPermissions.value.find(p => p.tool_id === tool.id)
      return permission && permission.can_admin
    })
  })

  const hasAnyTools = computed(() => activeTools.value.length > 0)

  const activeToolGroups = computed(() => {
    return toolGroups.value.filter(group => 
      group.is_active && group.tools.length > 0
    )
  })

  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
    if (loading) {
      error.value = null
    }
  }

  const setError = (errorMessage: string) => {
    error.value = errorMessage
    isLoading.value = false
  }

  // Alle verfügbaren Tools laden
  const loadAvailableTools = async (): Promise<boolean> => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated || !authStore.token) {
      tools.value = []
      return false
    }
    
    try {
      setLoading(true)
      
      console.log('[ToolsStore] Making API call with token:', !!authStore.token)
      console.log('[ToolsStore] Actual token value:', authStore.token)
      console.log('[ToolsStore] Authorization header:', `Bearer ${authStore.token}`)
      
      // DECODE JWT TOKEN TO CHECK TYPE
      if (authStore.token) {
        try {
          const tokenParts = authStore.token.split('.')
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]))
            console.log('[ToolsStore] JWT Token payload:', payload)
            console.log('[ToolsStore] Token type:', payload.type)
            console.log('[ToolsStore] Token expires:', new Date(payload.exp * 1000))
            console.log('[ToolsStore] Token is expired:', Date.now() > payload.exp * 1000)
          }
        } catch (e) {
          console.error('[ToolsStore] Failed to decode JWT token:', e)
        }
      }
      const toolsData = await apiClient.get('/v1/tools')
      
      console.log('[ToolsStore] Raw API response:', toolsData)
      console.log('[ToolsStore] Raw API response data:', toolsData.data)
      
      if (!toolsData.success) {
        console.error('[ToolsStore] API Error response:', toolsData)
        throw new Error(`Tools API Error: ${toolsData.message || 'Unknown error'}`)
      }
      
      if (toolsData.data && toolsData.data.length > 0) {
        console.log('[ToolsStore] First tool structure:', toolsData.data[0])
        console.log('[ToolsStore] First tool keys:', Object.keys(toolsData.data[0]))
      }
      
      // Tools mit Icon-Mapping und korrektem Field-Mapping
      const toolsWithIcons = toolsData.data.map((tool: any) => {
        console.log('[ToolsStore] Processing tool:', tool)
        console.log('[ToolsStore] Tool icon_svg from DB:', tool.icon_svg)
        console.log('[ToolsStore] Tool icon_svg type:', typeof tool.icon_svg)
        console.log('[ToolsStore] Tool icon_svg length:', tool.icon_svg?.length)
        
        const processedTool = {
          id: tool.id,
          name: tool.tool_name, // Backend: tool_name → Frontend: name
          description: tool.description || `${tool.tool_name} Tool`, // Fallback description
          path: tool.path,
          is_active: tool.active, // Backend: active → Frontend: is_active
          created_at: tool.created_at,
          updated_at: tool.updated_at,
          icon: tool.icon_svg || getToolIcon(tool.path || tool.tool_name), // Use AI-generated icon or fallback
          route: getToolRoute(tool.path || tool.tool_name)
        }
        console.log('[ToolsStore] Processed tool:', processedTool)
        console.log('[ToolsStore] Final icon value:', processedTool.icon)
        return processedTool
      })
      
      tools.value = toolsWithIcons
      setLoading(false)
      return true
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Tools'
      setError(errorMessage)
      console.error('Load tools error:', err)
      return false
    }
  }

  // User-spezifische Tool-Permissions laden
  const loadUserPermissions = async (): Promise<boolean> => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated || !authStore.token) {
      userPermissions.value = []
      return false
    }
    
    try {
      setLoading(true)
      
      console.log('[ToolsStore] Loading user permissions...')
      
      // Use the permission service
      const { permissionService } = await import('@/services/permissionService')
      const permissions = await permissionService.getUserToolPermissions()
      
      console.log('[ToolsStore] User permissions response:', permissions)
      
      if (Array.isArray(permissions)) {
        if (permissions.length > 0) {
          console.log('[ToolsStore] First permission structure:', permissions[0])
          console.log('[ToolsStore] First permission keys:', Object.keys(permissions[0]))
          console.log('[ToolsStore] Full permission object:', JSON.stringify(permissions[0], null, 2))
        }
        
        // Transform permissions to the expected format
        userPermissions.value = permissions.map((p: any) => {
          console.log('[ToolsStore] Processing permission:', p)
          console.log('[ToolsStore] Permission tool object:', p.tool)
          console.log('[ToolsStore] Permission tool type:', typeof p.tool)
          console.log('[ToolsStore] Permission tool is null:', p.tool === null)
          
          if (p.tool) {
            console.log('[ToolsStore] Tool object keys:', Object.keys(p.tool))
            console.log('[ToolsStore] Tool id:', p.tool.id)
            console.log('[ToolsStore] Tool tool_name:', p.tool.tool_name)
          }
          
          const processedPermission = {
            tool_id: p.tool?.id, // Backend: tool.id → Frontend: tool_id
            tool_name: p.tool?.tool_name || '', // Backend: tool.tool_name → Frontend: tool_name
            tool_path: p.tool?.path || '', // Backend: tool.path → Frontend: tool_path
            can_view: p.can_view,
            can_edit: p.can_edit,
            can_admin: p.can_admin,
            is_active: p.tool?.active || true // Backend: tool.active → Frontend: is_active
          }
          console.log('[ToolsStore] Processed permission:', processedPermission)
          return processedPermission
        })
        console.log('[ToolsStore] User permissions loaded:', userPermissions.value.length)
        return true
      } else {
        throw new Error('Invalid response format')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      console.error('[ToolsStore] Error loading user permissions:', errorMessage)
      setError(`Fehler beim Laden der Benutzerberechtigungen: ${errorMessage}`)
      userPermissions.value = []
      return false
    } finally {
      setLoading(false)
    }
  }

  // Tool-Gruppen mit zugänglichen Tools laden
  const loadToolGroups = async (): Promise<boolean> => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated || !authStore.token) {
      toolGroups.value = []
      return false
    }
    
    try {
      setLoading(true)
      
      console.log('[ToolsStore] Loading tool groups with accessible tools...')
      
      // TEMPORARY FALLBACK: Check if Groups API is available on production
      try {
        const response = await apiClient.get('/v1/tool-groups/with-tools')
        
        console.log('[ToolsStore] Tool groups API response:', response)
        
        if (!response.success) {
          throw new Error(`Tool Groups API Error: ${response.message || 'Unknown error'}`)
        }
        
        // Transform API response to frontend format
        const groupsWithTools = response.data.groups.map((group: any) => ({
          id: group.id,
          name: group.name,
          description: group.description || '',
          is_active: group.is_active,
          tools: group.tools.map((tool: any) => ({
            id: tool.id,
            name: tool.tool_name,
            description: tool.description || `${tool.tool_name} Tool`,
            path: tool.path,
            icon: tool.icon_svg || getToolIcon(tool.path || tool.tool_name),
            route: getToolRoute(tool.path || tool.tool_name),
            is_active: true, // Tools in groups are already filtered for accessibility
            permissions: {
              can_view: true,
              can_edit: false,
              can_admin: false
            }
          }))
        }))
        
        toolGroups.value = groupsWithTools
        console.log('[ToolsStore] Tool groups loaded:', toolGroups.value.length)
        
        setLoading(false)
        return true
        
      } catch (apiError) {
        console.warn('[ToolsStore] Groups API not available on production, using fallback:', apiError)
        
        // FALLBACK: Create default groups from available tools
        if (tools.value.length > 0) {
          const defaultGroup: ToolGroup = {
            id: 1,
            name: 'Verfügbare Tools',
            description: 'Alle verfügbaren Tools',
            is_active: true,
            tools: tools.value.filter(tool => tool.is_active).map(tool => ({
              id: tool.id,
              name: tool.name,
              description: tool.description,
              path: tool.path,
              icon: tool.icon,
              route: tool.route,
              is_active: tool.is_active,
              permissions: tool.permissions
            }))
          }
          
          toolGroups.value = defaultGroup.tools.length > 0 ? [defaultGroup] : []
          console.log('[ToolsStore] Using fallback tool groups:', toolGroups.value.length)
        } else {
          toolGroups.value = []
        }
        
        setLoading(false)
        return true
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Tool-Gruppen'
      setError(errorMessage)
      console.error('Load tool groups error:', err)
      toolGroups.value = []
      return false
    }
  }

  // Tools und Permissions laden
  const loadToolsAndPermissions = async (): Promise<boolean> => {
    console.log('[ToolsStore] Starting loadToolsAndPermissions...')
    
    const toolsLoaded = await loadAvailableTools()
    console.log('[ToolsStore] Tools loaded:', toolsLoaded, 'Tools count:', tools.value.length)
    
    const permissionsLoaded = await loadUserPermissions()
    console.log('[ToolsStore] Permissions loaded:', permissionsLoaded, 'Permissions count:', userPermissions.value.length)
    
    const groupsLoaded = await loadToolGroups()
    console.log('[ToolsStore] Tool groups loaded:', groupsLoaded, 'Groups count:', toolGroups.value.length)
    
    const success = toolsLoaded && permissionsLoaded && groupsLoaded
    console.log('[ToolsStore] loadToolsAndPermissions completed:', success)
    console.log('[ToolsStore] Final activeTools:', activeTools.value)
    console.log('[ToolsStore] Final activeToolGroups:', activeToolGroups.value)
    
    return success
  }

  // Tool-Icon basierend auf Path bestimmen
  const getToolIcon = (toolPath: string): string => {
    const iconMap: Record<string, string> = {
  //    'tocco-gen': '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"/></svg>',
      'user-management': '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.354a4 4 0 110 6.292 4 4 0 010-6.292zM15 8a3 3 0 11-6 0 3 3 0 016 0zm6 5c-.025-1.104-.652-2.124-1.618-2.618A1 1 0 0018 11h-1.382c-.618 0-1.235.214-1.618.618A3 3 0 0015 14v1a1 1 0 001 1h4a1 1 0 001-1v-1z"/></svg>',
      'admin': '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>'
    }
    
    return iconMap[toolPath] || '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>'
  }

  // Tool-Route basierend auf Path bestimmen
  const getToolRoute = (toolPath: string): string => {
    const routeMap: Record<string, string> = {
    //  'tocco-gen': '/tools/tocco-gen',
      'user-management': '/admin/users',
      'admin': '/admin'
    }
    
    return routeMap[toolPath] || `/tools/${toolPath}`
  }

  // Permission für bestimmtes Tool prüfen (by ID oder Name/Path)
  const hasToolPermission = (toolIdentifier: number | string, permission: 'view' | 'edit' | 'admin'): boolean => {
    console.log(`[ToolsStore] Checking permission for tool: ${toolIdentifier} (${permission})`)
    console.log(`[ToolsStore] Available permissions:`, userPermissions.value.map(p => ({
      tool_id: p.tool_id,
      tool_name: p.tool_name,
      tool_path: p.tool_path,
      can_view: p.can_view,
      can_edit: p.can_edit,
      can_admin: p.can_admin
    })))
    
    let toolPermission
    
    if (typeof toolIdentifier === 'number') {
      // Suche nach Tool-ID
      toolPermission = userPermissions.value.find(p => p.tool_id === toolIdentifier)
      console.log(`[ToolsStore] Searching by ID ${toolIdentifier}:`, toolPermission)
    } else {
      // ERWEITERTE SUCHE: Mehrere Varianten testen
      const identifier = toolIdentifier.toLowerCase()
      
      // 1. Exakte Übereinstimmung mit tool_name
      toolPermission = userPermissions.value.find(p => 
        p.tool_name?.toLowerCase() === identifier
      )
      console.log(`[ToolsStore] Exact tool_name match for '${identifier}':`, toolPermission)
      
      // 2. Exakte Übereinstimmung mit tool_path
      if (!toolPermission) {
        toolPermission = userPermissions.value.find(p => 
          p.tool_path?.toLowerCase() === identifier
        )
        console.log(`[ToolsStore] Exact tool_path match for '${identifier}':`, toolPermission)
      }
      
      // 3. Teilstring-Suche in tool_name
      if (!toolPermission) {
        toolPermission = userPermissions.value.find(p => 
          p.tool_name?.toLowerCase().includes(identifier) || 
          identifier.includes(p.tool_name?.toLowerCase() || '')
        )
        console.log(`[ToolsStore] Partial tool_name match for '${identifier}':`, toolPermission)
      }
      
      // 4. Teilstring-Suche in tool_path
      if (!toolPermission) {
        toolPermission = userPermissions.value.find(p => 
          p.tool_path?.toLowerCase().includes(identifier) || 
          identifier.includes(p.tool_path?.toLowerCase() || '')
        )
        console.log(`[ToolsStore] Partial tool_path match for '${identifier}':`, toolPermission)
      }
    }
    
    if (!toolPermission) {
      console.log(`[ToolsStore] No permission found for tool: ${toolIdentifier}`)
      return false
    }
    
    const permissionKey = `can_${permission}` as keyof typeof toolPermission
    const hasPermission = toolPermission[permissionKey] as boolean
    
    console.log(`[ToolsStore] Permission check result for ${toolIdentifier} (${permission}):`, hasPermission)
    console.log(`[ToolsStore] Found permission object:`, {
      tool_id: toolPermission.tool_id,
      tool_name: toolPermission.tool_name,
      tool_path: toolPermission.tool_path,
      can_view: toolPermission.can_view,
      can_edit: toolPermission.can_edit,
      can_admin: toolPermission.can_admin
    })
    
    return hasPermission
  }

  // Store zurücksetzen (bei Logout)
  const resetStore = () => {
    tools.value = []
    toolGroups.value = []
    userPermissions.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    tools,
    toolGroups,
    userPermissions,
    isLoading,
    error,
    
    // Getters
    activeTools,
    activeToolGroups,
    adminTools,
    hasAnyTools,
    
    // Actions
    setLoading,
    setError,
    loadAvailableTools,
    loadToolGroups,
    loadUserPermissions,
    loadToolsAndPermissions,
    hasToolPermission,
    resetStore
  }
})
